import {
  WSActions,
  WSAlert,
  WSCard,
  WSControl,
  WSControlGroupCardOption,
  WSControlGroupCards,
  WSInfoBox,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  PayoutDestinationType,
  PayoutPreferences
} from "@wingspanhq/payments/dist/interfaces";
import { IAccount } from "@wingspanhq/users/dist/lib/interfaces";
import { WSQueryCache } from "@ws-react-query";
import { useCallback, useMemo, useState } from "react";
import { useQueryInternalAccounts } from "../../../../query/banking/queries/useQueryInternalAccounts";
import { useWSMutation } from "../../../../query/helpers";
import { useUserId } from "../../../../query/hooks/helpers";
import { QUERY_PAYOUT_SETTINGS } from "../../../../query/payments/keys";
import { useQueryPayoutSettings } from "../../../../query/payments/queries";
import { paymentsService } from "../../../../services/payments";
import {
  avatarBankAccount,
  avatarBankCard,
  avatarWallet
} from "../../../../shared/constants/avatars";
import { selectorAccountInstitution } from "../../../../shared/selectors/selectorAccountInstitution";
import { selectorAccountName } from "../../../../shared/selectors/selectorAccountName";
import { selectorActiveBankingAccount } from "../../../../shared/selectors/selectorActiveBankingAccount";
import { selectorInstantPayoutDefaultFee } from "../../../../shared/selectors/selectorInstantPayoutDefaultFee";
import { selectorPendingBankingAccount } from "../../../../shared/selectors/selectorPendingBankingAccount";
import { isEligibleRoutingNumber } from "../../../../shared/utils/isEligibleRoutingNumber";
import { WSServiceError } from "../../../../utils/serviceHelper";
import { selectorAccountRtpReady } from "../../../Transfer/components/FormTransfer/selectorAccountRtpReady";

type AlternativeSelection = "debit-card" | "try-another-bank" | "wallet";

type Props = {
  accounts: IAccount[];
  onBack: () => void;
  onNext?: () => void;
  onDebitCard: () => void;
  onTryAnotherBank: () => void;
  onActivateWallet: () => void;
};

const isAccountEligible = (account: IAccount): boolean => {
  const routing = account.numbers?.routing;
  return routing
    ? isEligibleRoutingNumber(routing)
    : !!selectorAccountRtpReady(account);
};

export const RouteInstantAccountSelect: React.FC<Props> = props => {
  const userId = useUserId();
  const queryPayoutSettings = useQueryPayoutSettings(userId);
  const queryInternalAccounts = useQueryInternalAccounts();

  const hasActiveWallet = (queryInternalAccounts.data || []).some(
    selectorActiveBankingAccount
  );
  const hasPendingWallet = (queryInternalAccounts.data || []).some(
    selectorPendingBankingAccount
  );
  const fee = selectorInstantPayoutDefaultFee(queryPayoutSettings.data);

  const [selectedAccountId, setSelectedAccountId] = useState<
    string | undefined
  >(props.accounts[0]?.accountId);
  const [alternativeOption, setAlternativeOption] = useState<
    AlternativeSelection | undefined
  >(undefined);
  const [useAsStandard, setUseAsStandard] = useState(false);

  const selectedAccount = useMemo(
    () => props.accounts.find(a => a.accountId === selectedAccountId),
    [props.accounts, selectedAccountId]
  );

  const isSelectedEligible = selectedAccount
    ? selectorAccountRtpReady(selectedAccount)
    : true;

  const [setInstantPayout, setInstantPayoutMeta] = useWSMutation<
    void,
    WSServiceError,
    { accountId: string }
  >(
    async ({ accountId }) => {
      const payoutSettings = await paymentsService.payoutSettings.update(
        userId,
        {
          payoutPreferences: PayoutPreferences.Instant,
          instant: {
            destinationId: accountId,
            destinationType: PayoutDestinationType.Account
          }
        }
      );

      WSQueryCache.setQueryData(QUERY_PAYOUT_SETTINGS, payoutSettings);
    },
    {
      onSuccess: props.onNext,
      dependencies: [QUERY_PAYOUT_SETTINGS]
    }
  );

  const [setStandardPayout, setStandardPayoutMeta] = useWSMutation<
    void,
    WSServiceError
  >(
    async () => {
      if (!selectedAccountId) return;

      const payoutSettings = await paymentsService.payoutSettings.update(
        userId,
        {
          payoutPreferences: PayoutPreferences.Standard,
          standard: {
            destinationId: selectedAccountId,
            destinationType: PayoutDestinationType.Account
          }
        }
      );

      WSQueryCache.setQueryData(QUERY_PAYOUT_SETTINGS, payoutSettings);
    },
    {
      onSuccess: props.onNext,
      dependencies: [QUERY_PAYOUT_SETTINGS]
    }
  );

  const [setWalletAsInstantPayout, setWalletMeta] = useWSMutation<
    void,
    WSServiceError
  >(
    async () => {
      const activeWallet = (queryInternalAccounts.data || []).find(
        selectorActiveBankingAccount
      );
      if (!activeWallet) return;

      const payoutSettings = await paymentsService.payoutSettings.update(
        userId,
        {
          payoutPreferences: PayoutPreferences.Instant,
          instant: {
            destinationId: activeWallet.internalAccountId,
            destinationType: "InternalAccount" as PayoutDestinationType
          }
        }
      );

      WSQueryCache.setQueryData(QUERY_PAYOUT_SETTINGS, payoutSettings);
    },
    {
      onSuccess: props.onNext,
      dependencies: [QUERY_PAYOUT_SETTINGS]
    }
  );

  const handleContinue = useCallback(() => {
    if (!selectedAccountId) return;

    if (isSelectedEligible) {
      setInstantPayout({ accountId: selectedAccountId });
      return;
    }

    if (useAsStandard) {
      setStandardPayout();
      return;
    }

    if (alternativeOption === "debit-card") {
      props.onDebitCard();
    } else if (alternativeOption === "try-another-bank") {
      props.onTryAnotherBank();
    } else if (alternativeOption === "wallet") {
      if (hasActiveWallet) {
        setWalletAsInstantPayout();
      } else {
        props.onActivateWallet();
      }
    }
  }, [
    selectedAccountId,
    isSelectedEligible,
    useAsStandard,
    alternativeOption,
    hasActiveWallet,
    setInstantPayout,
    setStandardPayout,
    setWalletAsInstantPayout,
    props
  ]);

  const canContinue =
    isSelectedEligible || useAsStandard || !!alternativeOption;
  const isLoading =
    setInstantPayoutMeta.isLoading ||
    setStandardPayoutMeta.isLoading ||
    setWalletMeta.isLoading;

  const accountOptions = useMemo<WSControlGroupCardOption[]>(
    () =>
      props.accounts.map(account => ({
        header: {
          label: {
            text: selectorAccountInstitution(account),
            secondaryText: selectorAccountName(account),
            avatar: avatarBankAccount
          }
        },
        value: account.accountId
      })),
    [props.accounts]
  );

  return (
    <WSList gap="2XL">
      <WSCard
        header={{
          label: {
            text: "Set default payout method",
            helperText:
              "Select an account you added through Plaid as your payout method."
          }
        }}
      >
        <WSList gap="M">
          <WSControlGroupCards
            value={selectedAccountId ?? ""}
            onChange={newValue => {
              setSelectedAccountId(newValue);
              setAlternativeOption(undefined);
              setUseAsStandard(false);
            }}
            options={accountOptions}
          />

          {!isSelectedEligible && (
            <WSAlert
              theme="warning"
              icon="alert"
              title="Instant payout unavailable"
            >
              This account isn't eligible for instant payouts. Please select a
              different account or choose a standard payout method.
            </WSAlert>
          )}
        </WSList>
      </WSCard>

      {!isSelectedEligible && (
        <WSCard
          header={{
            label: {
              text: "Try another method"
            }
          }}
        >
          <WSList gap="2XL">
            <WSText.ParagraphSm color="gray500">
              Connect a debit card, use your Wingspan Wallet, try another bank,
              or switch to standard payout.
            </WSText.ParagraphSm>

            <WSControlGroupCards
              value={alternativeOption ?? ""}
              onChange={newValue => {
                setAlternativeOption(newValue as AlternativeSelection);
                setUseAsStandard(false);
              }}
              options={[
                {
                  header: {
                    label: {
                      text: `Connect a debit card (${fee}% fee)`,
                      helperText:
                        "Receive payments by connecting an eligible Visa or Mastercard debit card",
                      avatar: avatarBankCard
                    }
                  },
                  value: "debit-card"
                },
                {
                  header: {
                    label: {
                      text: hasActiveWallet
                        ? `Wingspan Wallet (${fee}% fee)`
                        : `Activate Wingspan Wallet (${fee}% fee)`,
                      helperText: hasActiveWallet
                        ? "Use your Wingspan Wallet as your instant payout method"
                        : "Business bank account with instant access to your funds",
                      avatar: avatarWallet
                    },
                    ...(hasPendingWallet
                      ? {
                          value: {
                            pill: {
                              theme: "blue" as const,
                              text: "Pending activation",
                              badge: true,
                              type: "noBackground" as const
                            },
                            stacked: true
                          }
                        }
                      : {})
                  },
                  disabled: hasPendingWallet,
                  value: "wallet"
                },
                {
                  header: {
                    label: {
                      text: "Try another bank",
                      helperText:
                        "Try instant payout by connecting an account from another bank",
                      avatar: avatarBankAccount
                    }
                  },
                  value: "try-another-bank"
                }
              ]}
            />

            <WSInfoBox theme="info">
              <WSText.ParagraphXs mb="L" color="gray500">
                If you wish to use the bank account you provided as a{" "}
                <b>standard payout method</b> instead, check the box below.
              </WSText.ParagraphXs>

              <WSControl
                type="checkbox"
                value={useAsStandard}
                onChange={checked => {
                  setUseAsStandard(checked);
                  if (checked) {
                    setAlternativeOption(undefined);
                  }
                }}
                label="Add this bank account and receive you funds in the standard 2-5 business days."
                size="S"
              />
            </WSInfoBox>
          </WSList>
        </WSCard>
      )}

      <WSActions
        alignment="fill"
        buttons={[
          {
            label: "Back",
            kind: "Tertiary",
            onClick: props.onBack
          },
          {
            label: "Continue",
            disabled: !canContinue,
            loading: isLoading,
            onClick: handleContinue
          }
        ]}
      />
    </WSList>
  );
};
